import type { ReactionPlanView } from '@minisoc/contracts';
import type { ReactNode } from 'react';
import { cn } from '../cn.js';
import { CommandBlock } from './code-block.js';
import { Card, CardHeader } from './primitives.js';

const PLAN_ICON = (
  <svg viewBox="0 0 24 24" className="size-4" fill="currentColor" aria-hidden="true">
    <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />
  </svg>
);

export interface ReactionPlanCardProps {
  readonly plan: ReactionPlanView;
  /**
   * Absent = consultation seule. Le personnel MiniSOC coche pour le compte du
   * client lors d'une levée de doute assistée ; l'API journalise l'auteur dans
   * les deux cas, la distinction reste donc traçable.
   */
  readonly onToggleStep?: (order: number, done: boolean) => void;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly subtitle?: ReactNode;
  readonly emptyMessage?: string;
}

/**
 * Plan de réaction, cœur de la promesse produit : rendre une alerte actionnable
 * en moins de 30 secondes par un administrateur non spécialiste (§12.2).
 *
 * Trois partis pris d'affichage :
 * - les étapes sont numérotées et cochables, pour servir de liste de contrôle
 *   pendant l'intervention ;
 * - les commandes sont copiables, jamais à retaper ;
 * - les étapes irréversibles sont signalées avant d'être exécutées, pas après.
 *
 * Partagé par les deux fronts : le dashboard client s'en sert comme procédure à
 * suivre, le backoffice comme référence pendant la qualification. Les libellés
 * qui s'adressent au client (« votre organisation ») sont donc surchargeables
 * plutôt que codés en dur.
 */
export function ReactionPlanCard({
  plan,
  onToggleStep,
  disabled = false,
  title = 'Plan de réaction',
  subtitle,
  emptyMessage = "Aucun plan de réaction n'est défini pour cette règle.",
}: ReactionPlanCardProps) {
  const readOnly = onToggleStep === undefined;
  const locked = disabled || readOnly;

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={
          subtitle ??
          (plan.customized ? 'Plan adapté à ce client' : 'Procédure de référence MiniSOC')
        }
        icon={PLAN_ICON}
      />

      <ol className="divide-y divide-border-subtle/60">
        {plan.steps.map((step) => (
          <li key={step.order} className="flex gap-3 px-5 py-4">
            <StepMarker
              order={step.order}
              completed={step.completed}
              locked={locked}
              onToggle={onToggleStep ? () => onToggleStep(step.order, !step.completed) : undefined}
            />

            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'text-sm leading-relaxed',
                  step.completed ? 'text-text-muted line-through decoration-text-dim' : 'text-text',
                )}
              >
                {step.instruction}
              </p>

              {step.destructive ? (
                <p className="mt-1.5 inline-flex items-center gap-1.5 rounded bg-high/10 px-2 py-1 text-xs text-high">
                  <span aria-hidden="true">⚠</span>
                  Action irréversible, à valider avec un responsable avant exécution.
                </p>
              ) : null}

              {step.command ? (
                <CommandBlock command={step.command} shell={step.shell} className="mt-2" />
              ) : null}
            </div>
          </li>
        ))}
      </ol>

      {plan.steps.length === 0 ? (
        <p className="px-5 py-6 text-sm text-text-muted">{emptyMessage}</p>
      ) : null}
    </Card>
  );
}

/**
 * Puce d'étape.
 *
 * En consultation seule le marqueur devient un simple `span` : un bouton
 * désactivé annoncerait au lecteur d'écran une action indisponible, alors qu'ici
 * il n'y a tout simplement pas d'action à proposer.
 */
function StepMarker({
  order,
  completed,
  locked,
  onToggle,
}: {
  order: number;
  completed: boolean;
  locked: boolean;
  onToggle?: () => void;
}) {
  const shape = cn(
    'mt-0.5 grid size-6 shrink-0 place-items-center rounded-full font-mono text-xs font-bold transition-colors',
    completed
      ? 'bg-success/20 text-success ring-1 ring-success/40'
      : 'bg-brand-500/10 text-brand-400 ring-1 ring-brand-500/30',
  );

  if (!onToggle) {
    return (
      <span className={shape} aria-hidden="true">
        {completed ? '✓' : order}
      </span>
    );
  }

  return (
    <button
      type="button"
      disabled={locked}
      onClick={onToggle}
      aria-pressed={completed}
      aria-label={`Étape ${order} ${completed ? 'faite' : 'à faire'}`}
      className={cn(
        shape,
        !completed && 'hover:bg-brand-500/20',
        locked && 'cursor-not-allowed opacity-60',
      )}
    >
      {completed ? '✓' : order}
    </button>
  );
}
